import { useId, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowUpRight, BadgeCheck, Check, FileText } from "lucide-react";
import NotFound from "@/pages/NotFound";
import {
  FotoCredito,
  SiteLayout,
  PhotoHero,
} from "@/components/site/SiteLayout";
import {
  buildParqueBreadcrumbJsonLd,
  buildParqueJsonLd,
} from "@/components/parques/parqueJsonLd";
import {
  getParqueBySlug,
  parquePath,
  relatedParques,
  type Parque as ParqueData,
  type ParqueTarifa,
} from "@/content/parques";
import { formatCOP } from "@/constants";
import { formatDateEs } from "@/lib/site";
import tayronaFoto from "@/assets/tayrona-hero.avif";

/**
 * /parques/:slug — one park guide (DESIGN.md).
 *
 * Order: photo hero, H1 + intro, fee panel (park color), how to get there,
 * hours and season, what to do, rules, related parks, sources, closing
 * panel. Every section hides itself when the official data is empty.
 */

const feeText = (valor: number): string => (valor === 0 ? "No paga" : formatCOP(valor));

/** "Caño Cristales: tarifas, cómo llegar..." -> ["Caño Cristales", "tarifas, cómo llegar..."] */
function splitH1(h1: string): [string, string | undefined] {
  const i = h1.indexOf(":");
  return i === -1 ? [h1, undefined] : [h1.slice(0, i), h1.slice(i + 1).trim()];
}

function FeePicker({ tarifas }: { tarifas: ParqueTarifa[] }) {
  const [index, setIndex] = useState(0);
  const name = useId();

  return (
    <div style={{ display: "grid", gap: 24 }}>
      <fieldset className="pq-seg">
        <legend>Elige tu caso</legend>
        {tarifas.map((t, i) => (
          <label key={t.grupo}>
            <input
              type="radio"
              name={name}
              value={i}
              checked={index === i}
              onChange={() => setIndex(i)}
            />
            {t.grupo}
          </label>
        ))}
      </fieldset>
      <div aria-live="polite">
        <p className="pq-panel-note">Valor por persona en 2026</p>
        <p className="pq-price">{feeText(tarifas[index].valor)}</p>
      </div>
    </div>
  );
}

function Checklist({ items }: { items: string[] }) {
  return (
    <ul className="pq-checks">
      {items.map((item) => (
        <li key={item}>
          <Check size={18} strokeWidth={2.25} aria-hidden="true" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

const Parque = () => {
  const { slug } = useParams<{ slug: string }>();
  const parque = getParqueBySlug(slug);
  if (!parque) return <NotFound />;

  // Visible H1 is just the place name; the full keyword phrase stays in <title>.
  const [nombre] = splitH1(parque.h1);
  const related = relatedParques(parque);
  const { portada, detalle } = parque.fotos;

  return (
    <SiteLayout
      current="parques"
      tema={parque.tema}
      ctaHref={parque.accesos.length ? "#como-llegar" : undefined}
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildParqueJsonLd(parque)) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(buildParqueBreadcrumbJsonLd(parque)),
        }}
      />

      <PhotoHero
        foto={portada}
        credit={<FotoCredito foto={portada} />}
      />

      <section className="pq-wrap pq-intro">
        <nav aria-label="Ruta de navegación" className="pq-crumbs pq-rise">
          <a href="/">Inicio</a>
          <span aria-hidden="true">/</span>
          <a href="/parques">Parques</a>
          <span aria-hidden="true">/</span>
          <span aria-current="page">{parque.atractivoPrincipal}</span>
        </nav>
        <h1 className="pq-display pq-rise pq-rise-2">{nombre}.</h1>
        <p className="pq-lead pq-rise pq-rise-3">{parque.intro}</p>
      </section>

      {parque.tarifas.length ? (
        <section id="tarifas" aria-labelledby="tarifas-h" className="pq-section">
          <div className="pq-wrap">
            <div className="pq-panel">
              <div className="pq-panel-body">
                <h2 id="tarifas-h" className="pq-headline">Cuánto cuesta entrar.</h2>
                <FeePicker tarifas={parque.tarifas} />
                <ul className="pq-fee-list" aria-label="Todas las tarifas">
                  {parque.tarifas.map((t) => (
                    <li key={t.grupo}>
                      <span>{t.grupo}</span>
                      <span>{feeText(t.valor)}</span>
                    </li>
                  ))}
                </ul>
                {parque.tarifasNota ? (
                  <p className="pq-panel-note">{parque.tarifasNota}</p>
                ) : null}
              </div>
              <figure className="pq-panel-figure">
                <img
                  src={detalle.src}
                  alt={detalle.alt}
                  width={detalle.width}
                  height={detalle.height}
                  loading="lazy"
                  decoding="async"
                />
                <div className="pq-panel-chips pq-float" aria-hidden="true">
                  {parque.seguro ? (
                    <span className="pq-chip pq-chip-frost">Revisa el seguro</span>
                  ) : null}
                  <span className="pq-chip pq-chip-frost">Valor único todo el año</span>
                  {parque.operadores.length || parque.operadoresLista ? (
                    <span className="pq-chip pq-chip-frost">Con operador autorizado</span>
                  ) : null}
                </div>
                <figcaption className="pq-figcredit">
                  <FotoCredito foto={detalle} />
                </figcaption>
              </figure>
            </div>
          </div>
        </section>
      ) : null}

      {parque.accesos.length ? (
        <section id="como-llegar" aria-labelledby="llegar-h" className="pq-section-tight">
          <div className="pq-wrap" style={{ display: "grid", gap: 40 }}>
            <h2 id="llegar-h" className="pq-headline">Cómo llegar.</h2>
            <div className="pq-row">
              {parque.accesos.map((acceso) => (
                <article key={acceso.desde} className="pq-card">
                  <h3 className="pq-title">Desde {acceso.desde}</h3>
                  <p className="pq-small">{acceso.como}</p>
                  {acceso.tiempo ? <p className="pq-meta">{acceso.tiempo}</p> : null}
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {parque.horarios?.length || parque.temporada || parque.cierres.length ? (
        <section aria-labelledby="horarios-h" className="pq-section pq-mist">
          <div className="pq-wrap" style={{ display: "grid", gap: 40 }}>
            <h2 id="horarios-h" className="pq-headline">Horarios y temporada.</h2>
            <div className="pq-row">
              {parque.horarios?.length ? (
                <article className="pq-card">
                  <h3 className="pq-title">Horarios</h3>
                  {parque.horarios.map((h) => (
                    <p key={h} className="pq-small">{h}</p>
                  ))}
                </article>
              ) : null}
              {parque.temporada ? (
                <article className="pq-card">
                  <h3 className="pq-title">Temporada</h3>
                  <p className="pq-small">{parque.temporada}</p>
                </article>
              ) : null}
              {parque.cierres.length ? (
                <article className="pq-card">
                  <h3 className="pq-title">Cierres</h3>
                  {parque.cierres.map((c) => (
                    <p key={c} className="pq-small">{c}</p>
                  ))}
                </article>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {parque.actividades.length || parque.atractivos.length ? (
        <section aria-labelledby="hacer-h" className="pq-section">
          <div className="pq-wrap" style={{ display: "grid", gap: 32 }}>
            <h2 id="hacer-h" className="pq-headline">Qué hacer.</h2>
            {parque.actividades.length ? (
              <ul className="pq-tags" aria-label="Actividades permitidas">
                {parque.actividades.map((a) => (
                  <li key={a} className="pq-chip">{a}</li>
                ))}
              </ul>
            ) : null}
            {parque.atractivos.length ? (
              <div style={{ display: "grid", gap: 16 }}>
                <h3 className="pq-title">Lugares para visitar</h3>
                <Checklist items={parque.atractivos} />
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {parque.reglas.length || parque.seguro || parque.operadores.length || parque.operadoresLista ? (
        <section aria-labelledby="reglas-h" className="pq-section-tight">
          <div className="pq-wrap" style={{ display: "grid", gap: 32 }}>
            <h2 id="reglas-h" className="pq-headline">Antes de entrar.</h2>
            {parque.reglas.length ? <Checklist items={parque.reglas} /> : null}
            <div className="pq-row">
              {parque.seguro ? (
                <article className="pq-card">
                  <h3 className="pq-title">Seguro</h3>
                  <p className="pq-small">{parque.seguro}</p>
                </article>
              ) : null}
              {parque.operadores.length || parque.operadoresLista ? (
                <article className="pq-card">
                  <h3 className="pq-title">Operadores</h3>
                  {parque.operadores.map((o) => (
                    <p key={o} className="pq-small">{o}</p>
                  ))}
                  {parque.operadoresLista ? (
                    <p className="pq-small">
                      <a
                        href={parque.operadoresLista.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pq-link"
                      >
                        {parque.operadoresLista.label}
                      </a>
                    </p>
                  ) : null}
                </article>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {related.length ? (
        <section aria-labelledby="otros-h" className="pq-section">
          <div className="pq-wrap" style={{ display: "grid", gap: 40 }}>
            <h2 id="otros-h" className="pq-headline">Otros parques.</h2>
            <div className="pq-row">
              {related.map((p) => (
                <a key={p.slug} href={parquePath(p)} className="pq-parkcard">
                  <div className="pq-parkcard-img">
                    <img
                      src={p.fotos.portada.src}
                      alt={p.fotos.portada.alt}
                      width={p.fotos.portada.width}
                      height={p.fotos.portada.height}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <span className="pq-title">{p.atractivoPrincipal}</span>
                  <span className="pq-meta">{p.departamentos.join(", ")}</span>
                </a>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section aria-labelledby="fuentes-h" className="pq-wrap pq-sources-wrap">
        <div className="pq-sources">
          <div className="pq-sources-intro">
            <BadgeCheck size={28} strokeWidth={1.75} aria-hidden="true" />
            <h2 id="fuentes-h" className="pq-title">Datos oficiales</h2>
            <p className="pq-small">
              Revisados el{" "}
              <time dateTime={parque.verificadoEl}>{formatDateEs(parque.verificadoEl)}</time>{" "}
              en el sitio de Parques Nacionales Naturales de Colombia. Guía
              independiente, sin afiliación con Parques Nacionales.
            </p>
          </div>
          <ul className="pq-sources-list">
            {parque.fuentes.map((fuente) => (
              <li key={fuente.url}>
                <a href={fuente.url} target="_blank" rel="noopener noreferrer">
                  <FileText size={18} strokeWidth={1.75} aria-hidden="true" />
                  <span>{fuente.label}</span>
                  <ArrowUpRight size={18} aria-hidden="true" />
                  <span className="pq-sr"> (abre en una pestaña nueva)</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section aria-labelledby="cierre-h" className="pq-wrap" style={{ paddingBottom: "var(--pq-section)" }}>
        <div className="pq-closing">
          <img
            src={tayronaFoto}
            alt="Vista panorámica del Parque Nacional Natural Tayrona, Colombia"
            loading="lazy"
            decoding="async"
          />
          <div className="pq-closing-body">
            <h2 id="cierre-h" className="pq-headline">¿Y el Tayrona?</h2>
            <p>
              Si buscas acampar frente al mar, comparamos los campings del
              Parque Tayrona con precios y fechas de cierre.
            </p>
            <a href="/#campsites" className="pq-btn pq-btn-light">
              Ver campings del Tayrona
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default Parque;
